/**
 * 문 배치 계산 (세계 좌표: 가로 면은 x, 세로 면은 y)
 * - 면(face): 외곽 경계선 위 변의 안쪽 면 구간 — 양 끝은 볼록 꼭짓점(코너·하리)이라 PT씩 안쪽
 * - 구간(slot): 면에서 다른 문을 뺀 빈 구간. 문은 구간 안에서 양옆 남는 패널이 최소(200) 이상이어야 함
 */
import { Pt, FillOpts, fillLength } from '../engine/polyEngine';
import { Door } from '../types';

export type WallSide = Door['wallSide'];
export interface Range { lo: number; hi: number }

/** 문 로컬 위치 ↔ 세계 좌표 오프셋 (기존 데이터 규약: 상단·우측은 PT부터) */
export const doorOffset = (side: WallSide, PT: number) => (side === 'top' || side === 'right' ? PT : 0);
export const doorLen = (d: Pick<Door, 'leftPanel' | 'width' | 'rightPanel'>) => d.leftPanel + d.width + d.rightPanel;
export const doorFrom = (d: Door, PT: number) => doorOffset(d.wallSide, PT) + d.position;

/** 외곽 경계선(side) 위 변들의 안쪽 면 구간 */
export function sideFaces(outline: Pt[], side: WallSide, H: number, V: number, PT: number): Range[] {
  const horiz = side === 'top' || side === 'bottom';
  const line = side === 'top' ? 0 : side === 'bottom' ? V : side === 'left' ? 0 : H;
  const out: Range[] = [];
  for (let i = 0; i < outline.length; i++) {
    const a = outline[i], b = outline[(i + 1) % outline.length];
    if (horiz ? a.y !== line || b.y !== line : a.x !== line || b.x !== line) continue;
    const lo = Math.min(horiz ? a.x : a.y, horiz ? b.x : b.y) + PT;
    const hi = Math.max(horiz ? a.x : a.y, horiz ? b.x : b.y) - PT;
    if (hi > lo) out.push({ lo, hi });
  }
  return out.sort((p, q) => p.lo - q.lo);
}

/** 면에서 다른 문(세계 구간)을 뺀 빈 구간들 */
export function freeSlots(faces: Range[], others: Range[]): Range[] {
  const slots: Range[] = [];
  faces.forEach(f => {
    let cur = f.lo;
    others.filter(o => o.hi > f.lo && o.lo < f.hi).sort((p, q) => p.lo - q.lo).forEach(o => {
      if (o.lo > cur) slots.push({ lo: cur, hi: o.lo });
      cur = Math.max(cur, o.hi);
    });
    if (f.hi > cur) slots.push({ lo: cur, hi: f.hi });
  });
  return slots;
}

export interface DoorCheck {
  ok: boolean;
  reason?: string;
  slot?: Range;      // 문이 놓인 빈 구간 (세계 좌표)
  gapLo: number;     // 왼/위 남는 길이 (구간 시작 → 문 시작)
  gapHi: number;     // 오른/아래 남는 길이 (문 끝 → 구간 끝)
}

const loName = (side: WallSide) => (side === 'top' || side === 'bottom' ? '왼쪽' : '위쪽');
const hiName = (side: WallSide) => (side === 'top' || side === 'bottom' ? '오른쪽' : '아래쪽');

/** 문 구간 [from, from+len] 검사 */
export function checkDoor(side: WallSide, from: number, len: number, faces: Range[], others: Range[], min: number): DoorCheck {
  const to = from + len, mid = (from + to) / 2;
  const hitOther = others.find(o => o.lo < to && o.hi > from);
  if (hitOther) return { ok: false, reason: '다른 문과 겹침', gapLo: 0, gapHi: 0 };
  const face = faces.find(f => mid >= f.lo && mid <= f.hi);
  if (!face) return { ok: false, reason: '벽이 없는 위치 (하리·홈)', gapLo: 0, gapHi: 0 };
  const slot = freeSlots([face], others).find(s => mid >= s.lo && mid <= s.hi)!;
  const gapLo = from - slot.lo, gapHi = slot.hi - to;
  if (gapLo < 0) return { ok: false, reason: `${loName(side)} 끝(코너·하리·문)을 넘어감`, slot, gapLo, gapHi };
  if (gapHi < 0) return { ok: false, reason: `${hiName(side)} 끝(코너·하리·문)을 넘어감`, slot, gapLo, gapHi };
  if (gapLo < min) return { ok: false, reason: `${loName(side)} 남는 패널 ${gapLo}mm — 최소 ${min}`, slot, gapLo, gapHi };
  if (gapHi < min) return { ok: false, reason: `${hiName(side)} 남는 패널 ${gapHi}mm — 최소 ${min}`, slot, gapLo, gapHi };
  return { ok: true, slot, gapLo, gapHi };
}

/** 원하는 시작 위치에서 가장 가까운 놓을 수 있는 시작 위치 (없으면 null) */
export function nearestValidFrom(from: number, len: number, faces: Range[], others: Range[], min: number): number | null {
  let best: number | null = null;
  freeSlots(faces, others).forEach(s => {
    const a = s.lo + min, b = s.hi - min - len;
    if (b < a) return;
    const c = Math.max(a, Math.min(b, from));
    if (best === null || Math.abs(c - from) < Math.abs(best - from)) best = c;
  });
  return best;
}

/** 900짜리만으로 딱 떨어지는 조각 길이들 (1장 900, 2장 1808 …) */
export function fullPanelTotals(fill: FillOpts, upTo: number): { total: number; count: number }[] {
  const out: { total: number; count: number }[] = [];
  for (let k = 1; k * fill.max <= upTo; k++) {
    for (let t = k * fill.max; t <= k * (fill.max + fill.space) + fill.space; t++) {
      const s = fillLength(t, fill);
      if (s.length === k && s.every(v => v === fill.max)) { out.push({ total: t, count: k }); break; }
    }
  }
  return out;
}
